import React, { useMemo, Fragment, useEffect, useState } from "react";
import {
  MaterialReactTable,
  MRT_FullScreenToggleButton as MRTFullScreenToggleButton,
  MRT_ShowHideColumnsButton as MRTShowHideColumnsButton,
  MRT_ToggleFiltersButton as MRTToggleFiltersButton,
} from "material-react-table";
import Typography from "@mui/material/Typography";
import EditIcon from "@mui/icons-material/Edit";
import { useViewMeetingRequestsListMutation } from "../../../reactQueries/AppApi/rqHooksForMeetingRequests";
import { dateformatConverter } from "../../../common/utilities/dates/dateformatConverter";
import { meetingReqEditPath } from "../../../routes/routePaths";
import { useNavigate } from "react-router-dom";
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import MpgErrorDialog from "../../../common/dialog/MpgErrorDialog";
import NoTableDataComp from "../../../common/otherCommonComponents/NoTableDataComp";
import HtmlTooltip from "../../../common/tooltips/HtmlTooltip";
import { useGetLoggedInUserDetails } from "../../../hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { useAccessTypes } from "../../../hooksAndFunctions/customHooks/useAccessTypes";
import ExportButton from "../../../common/otherCommonComponents/ExportButton";
import {
  useGetSiteCoreCMSData,
  useTrackActivityMutation,
} from "../../../reactQueries/commonRQHooks";

const TooltipCell = ({ renderedCellValue }) => (
  <HtmlTooltip title={renderedCellValue}>{renderedCellValue}</HtmlTooltip>
);

const returnLabel = (key, labelArray = []) => {
  return labelArray.find((label) => label.Key === key)?.Phrase || '';
};

const ViewMeetingRequests = () => {

  const navigate = useNavigate()

  const { loggedInUser } = useGetLoggedInUserDetails();
  const { clientPortalAdminType: { hasA1Access } } = useAccessTypes();
  const { clientPortalAdminRoleId, isEnterpriseAdmin, id: userId } = loggedInUser;

  const canExportXls = isEnterpriseAdmin || hasA1Access;

  const { mutate: getMeetingRequestList, isSuccess, data: meetingRequestsData, isError, isLoading: isLoadingOnMeetingReq } = useViewMeetingRequestsListMutation();
  const { data: adminMeetingReqCMSDataFromAPI } = useGetSiteCoreCMSData({pagename: "MeetingRequests"});
  const { mutate: trackActivityMutate } = useTrackActivityMutation();

  const [meetingReqCMSData, setMeetingReqCMSData] = useState({
    title: "",
    description: "",
    tabNavigationTitle: "",
    pageContent: {},
  });

  useEffect(() => {
    getMeetingRequestList({ loggedInUserId: userId, roleId: clientPortalAdminRoleId, isEnterpriseAdmin })
  }, [loggedInUser])

  const fetchMeetingReqCMSData = () =>{
    adminMeetingReqCMSDataFromAPI?.Pages?.forEach((el) => {
       if (el.Key === "MeetingRequests") {
         const { Title, Description, TabNavigationTitle, Components } = el;
         setMeetingReqCMSData({ title: Title, description: Description, tabNavigationTitle: TabNavigationTitle, pageContent: Components });
       }
    });
  }

  const fetchMeetingReqListLabel = (key) => {
    const labels = meetingReqCMSData?.pageContent?.MeetingRequestsListComponent?.Labels;
    return returnLabel(key, labels);
  }

  const fetchCommonCMSLabel = (key) => {
    const labels = meetingReqCMSData?.pageContent?.CommonComponent?.Labels;
    return returnLabel(key, labels);
  }

  const meetingRequestsTitle = meetingReqCMSData?.title;
  const exportFileName = `${meetingRequestsTitle }.xlsx`;
  const exportWorksheetName = meetingRequestsTitle;

  const exportColumns = [
    { id: 'meetingTitle', header: fetchMeetingReqListLabel("MeetingRequestsList.MeetingTitle"), getValue: (row) => row?.Title || '' },
    { id: 'clientAccountName', header: fetchMeetingReqListLabel("MeetingRequestsList.ClientAccount"), getValue: (row) => row?.ClientAccountName || '' },
    { id: 'clientSubAccountName', header: fetchMeetingReqListLabel("MeetingRequestsList.ClientSubAccount"), getValue: (row) => row?.ClientSubAccountName || '' },
    { id: 'MeetingStatusDescription', header: fetchMeetingReqListLabel("MeetingRequestsList.Status"), getValue: (row) => row?.MeetingStatusDescription || '' },
    { id: 'RequestedFor', header: fetchMeetingReqListLabel("MeetingRequestsList.RequestedFor"), getValue: (row) => row?.RequestedFor || '' },
    { id: 'Requestedby', header: fetchMeetingReqListLabel("MeetingRequestsList.RequestedBy"), getValue: (row) => row?.Requestedby || '' },
    { id: 'requestedOn', header: fetchMeetingReqListLabel("MeetingRequestsList.RequestedOn"), getValue: (row) => row?.MeetingDate ? dateformatConverter(row.MeetingDate, "DD MMM YYYY hh:mm A") : '' },
    { id: 'submittedOn', header: fetchMeetingReqListLabel("MeetingRequestsList.SubmittedOn"), getValue: (row) => row?.CreatedTimeStamp ? dateformatConverter(row.CreatedTimeStamp, "DD MMM YYYY hh:mm A") : '' },
  ];


  useEffect(() => {
    fetchMeetingReqCMSData();
   }, [adminMeetingReqCMSDataFromAPI]);

  const columns = useMemo(
    () => [
      {
        id: "meetingTitle",
        header: fetchMeetingReqListLabel("MeetingRequestsList.MeetingTitle"), //"Meeting Title",
        accessorFn: (originalRow) => {
          const title = originalRow.Title
          return title || ""
        },
        Cell: TooltipCell,
        filterFn: 'contains',
        muiTableHeadCellFilterTextFieldProps: 
          { placeholder:fetchMeetingReqListLabel("MeetingRequestsList.iPlaceholderMeetingTitle") }
      },
      {
        accessorFn: (originalRow) => {
          const val = originalRow.ClientAccountName
          return val || ""
        },
        id: "clientAccountName",
        header: fetchMeetingReqListLabel("MeetingRequestsList.ClientAccount"), //"Client Account",
        Cell: TooltipCell,
        filterFn: 'contains',
        muiTableHeadCellFilterTextFieldProps: 
          { placeholder:fetchMeetingReqListLabel("MeetingRequestsList.PlaceholderClientAccount") }
      },
      {
        accessorFn: (originalRow) => {
          const val = originalRow.ClientSubAccountName
          return val || ""
        },
        id: "clientSubAccountName",
        header: fetchMeetingReqListLabel("MeetingRequestsList.ClientSubAccount"), //"Client Sub-Account",
        Cell: TooltipCell,
        filterFn: 'contains',
        muiTableHeadCellFilterTextFieldProps: 
          { placeholder:fetchMeetingReqListLabel("MeetingRequestsList.PlaceholderClientSubAccount") }
      },
      {
        accessorKey: "MeetingStatusDescription",
        header: fetchMeetingReqListLabel("MeetingRequestsList.Status"), //"Status",
        filterFn: 'contains',
        muiTableHeadCellFilterTextFieldProps: 
          { placeholder:fetchMeetingReqListLabel("MeetingRequestsList.PlaceholderStatus") }
      },
      {
        accessorKey: "RequestedFor",
        header: fetchMeetingReqListLabel("MeetingRequestsList.RequestedFor"), //"Requested For",
        Cell: TooltipCell,
        filterFn: 'contains',
        muiTableHeadCellFilterTextFieldProps: 
          { placeholder:fetchMeetingReqListLabel("MeetingRequestsList.PlaceholderRequestedFor") }
      },
      {
        accessorKey: "Requestedby",
        header: fetchMeetingReqListLabel("MeetingRequestsList.RequestedBy"), //"Requested By",
        Cell: TooltipCell,
        filterFn: 'contains',
        muiTableHeadCellFilterTextFieldProps: 
          { placeholder:fetchMeetingReqListLabel("MeetingRequestsList.PlaceholderRequestedBy") }
      },
      {
        accessorFn: (originalRow) => {
          const data = dateformatConverter(originalRow.MeetingDate, "DD MMM YYYY hh:mm A");
          return `${data}` || ""
        },
        id: 'requestedOn',
        header: fetchMeetingReqListLabel("MeetingRequestsList.RequestedOn"), //"Requested On",
        filterFn: 'contains',
        sortingFn: 'datetime',
        muiTableHeadCellFilterTextFieldProps: 
          { placeholder:fetchMeetingReqListLabel("MeetingRequestsList.PlaceholderRequestedOn") }
      },
      {
        accessorFn: (originalRow) => {
          const data = dateformatConverter(originalRow.CreatedTimeStamp, "DD MMM YYYY hh:mm A");
          return `${data}` || ""
        },
        id: 'submittedOn',
        header: fetchMeetingReqListLabel("MeetingRequestsList.SubmittedOn"), //"Submitted On",
        filterFn: 'contains',
        sortingFn: 'datetime',
        muiTableHeadCellFilterTextFieldProps: 
        { placeholder:fetchMeetingReqListLabel("MeetingRequestsList.PlaceholderSubmittedOn") }
      },
    ],
    [meetingReqCMSData]
  );

  return (
    <Fragment>
      {
        isLoadingOnMeetingReq && fetchCommonCMSLabel('Common.Waitloading')
      }

      <Typography variant="h5" align="center" sx={{ fontWeight: "bolder" }}>
        {meetingReqCMSData?.title}
      </Typography>

      {/**incase successful call but empty response */}
      {isSuccess && !meetingRequestsData?.data?.length && (<NoTableDataComp />)}
      {isSuccess && meetingRequestsData?.data?.length > 0 && (
        <MaterialReactTable
          columns={columns}
          data={meetingRequestsData?.data || []}
          layoutMode="grid"
          enableGlobalFilter={false}
          enableDensityToggle={false}

          initialState={{
            density: "compact",
            showColumnFilters: true,
          }}
          state={{
            isLoading: isLoadingOnMeetingReq,
            columnPinning: {
              left: ['mrt-row-actions', 'meetingTitle']
            }
          }}
          enableStickyHeader
          enableRowActions
          displayColumnDefOptions={{
            'mrt-row-actions': {
              header: fetchMeetingReqListLabel('MeetingRequestsList.Actions'), //Actions
            },
          }}
          muiTablePaginationProps={{
            labelRowsPerPage: fetchCommonCMSLabel('Common.Rowsperpage') //Rows per page
          }}
          renderToolbarInternalActions={({ table }) => (
            <>
              {canExportXls && (
                <ExportButton
                  table={table}
                  tooltipTitle={fetchMeetingReqListLabel("MeetingRequestsList.ExportToXLS")}
                  trackActivity={trackActivityMutate}
                  userId={userId}
                  exportColumns={exportColumns}
                  fileName={exportFileName}
                  worksheetName={exportWorksheetName}
                />
              )}
              <MRTToggleFiltersButton table={table} />
              <MRTShowHideColumnsButton table={table} />
              <MRTFullScreenToggleButton table={table} />
            </>
          )}
          renderRowActions={({ row, table }) => {
            const Id = row.original.Id;
            return (
              <Box sx={{ display: 'flex', flexWrap: 'nowrap', gap: '8px' }}>
                <IconButton
                  onClick={() => {
                    navigate(meetingReqEditPath({ id: Id }));
                  }}
                >
                  <EditIcon />
                </IconButton>
              </Box>
            )

          }}
        />
      )}

      <MpgErrorDialog
        open={isError}
      />
    </Fragment>
  );
};

export default ViewMeetingRequests;
